import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import useApiEndpoints from './useApiEndpoints';
import { useAuthStore } from './AuthContext';
import getContext from './AppSettingsContext';
import { RoleTypeEnum } from '../enums/roleTypeEnum';

export type Severity = 'Information' | 'Warning' | 'Critical';

export interface StatusMessage {
    id: number;
    severity: Severity;
    message: string;
    isActive: boolean;
    createdAt: string;
    createdBy: string | null;
    deactivatedAt: string | null;
    deactivatedBy: string | null;
}

export interface ReleaseNote {
    id: number;
    title: string;
    content: string;
    releaseDate: string;
    isPublished: boolean;
    isDeleted: boolean;
    createdDate: string;
    modifiedDate?: string;
    createdBy: string;
    modifiedBy?: string;
}

export interface MaintenanceModeStatus {
    isMaintenanceActive: boolean;
    isMaintenanceScheduled: boolean;
    message: string | null;
    startsInMinutes: number | null;
    estimatedMinutesRemaining: number | null;
    activatedBy: string | null;
    activatedDate: string | null;
    scheduledStartTimeUtc: string | null;
    estimatedEndTimeUtc: string | null;
}

interface AppStatusResponse {
    releaseNotes: ReleaseNote[];
    maintenanceMode: MaintenanceModeStatus | null;
    statusMessages: StatusMessage[];
}

interface AppStatusContextType {
    releaseNotes: ReleaseNote[];
    maintenanceMode: MaintenanceModeStatus | null;
    activeStatusMessages: StatusMessage[];
    isLoading: boolean;
    error: string | null;
    apiUnavailable: boolean;
    refetch: () => void;
}

const MAINTENANCE_STORAGE_KEY = 'orcaweb_maintenance_status';
const MAINTENANCE_TTL_MS = 40 * 60 * 1000; // 40 minutes
const MAINTENANCE_PAST_THRESHOLD_MS = 20 * 60 * 1000; // 20 min past end time
const RETRY_BACKOFF_MS = [5000, 15000, 30000]; // 5s, 15s, 30s cap

const MAINTENANCE_EXEMPT_ENDPOINTS = [
    'GetAppStatus',
    'SetMaintenanceMode',
    'negotiate',
    'signalr',
    'JoinGroup',
    'LeaveGroup',
    'GetRole',
    'GetAppFeatures',
];

interface StoredMaintenance {
    status: MaintenanceModeStatus;
    savedAt: number;
}

function readMaintenanceFromStorage(): MaintenanceModeStatus | null {
    try {
        const raw = localStorage.getItem(MAINTENANCE_STORAGE_KEY);
        if (!raw) return null;
        const parsed: StoredMaintenance = JSON.parse(raw);
        if (!parsed?.status || typeof parsed.savedAt !== 'number') {
            localStorage.removeItem(MAINTENANCE_STORAGE_KEY);
            return null;
        }
        if (Date.now() - parsed.savedAt > MAINTENANCE_TTL_MS) {
            localStorage.removeItem(MAINTENANCE_STORAGE_KEY);
            return null;
        }
        if (parsed.status.estimatedEndTimeUtc) {
            const endTime = new Date(parsed.status.estimatedEndTimeUtc).getTime();
            if (endTime < Date.now() - MAINTENANCE_PAST_THRESHOLD_MS) {
                localStorage.removeItem(MAINTENANCE_STORAGE_KEY);
                return null;
            }
        }
        return parsed.status;
    } catch {
        localStorage.removeItem(MAINTENANCE_STORAGE_KEY);
        return null;
    }
}

function writeMaintenanceToStorage(status: MaintenanceModeStatus | null): void {
    if (!status || !status.isMaintenanceActive) {
        localStorage.removeItem(MAINTENANCE_STORAGE_KEY);
        return;
    }
    try {
        const stored: StoredMaintenance = { status, savedAt: Date.now() };
        localStorage.setItem(MAINTENANCE_STORAGE_KEY, JSON.stringify(stored));
    } catch {
        // ignore storage errors
    }
}

const AppStatusContext = createContext<AppStatusContextType>({
    releaseNotes: [],
    maintenanceMode: null,
    activeStatusMessages: [],
    isLoading: false,
    error: null,
    apiUnavailable: false,
    refetch: () => {},
});

export const AppStatusProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [releaseNotes, setReleaseNotes] = useState<ReleaseNote[]>([]);
    const [maintenanceMode, setMaintenanceMode] = useState<MaintenanceModeStatus | null>(() => readMaintenanceFromStorage());
    const [activeStatusMessages, setActiveStatusMessages] = useState<StatusMessage[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [apiUnavailable, setApiUnavailable] = useState(false);
    const { apiEndpoints } = useApiEndpoints();
    const { loggedInUser, role } = useAuthStore();
    const appSettings = useContext(getContext());
    const fetchRef = useRef<() => Promise<void>>();
    const networkFailureCountRef = useRef(0);
    const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const roleRef = useRef(role);

    useEffect(() => {
        roleRef.current = role;
    }, [role]);

    // Register axios request interceptor — reads localStorage and roleRef to avoid stale closures
    useEffect(() => {
        const interceptorId = axios.interceptors.request.use((config) => {
            const stored = readMaintenanceFromStorage();
            if (!stored?.isMaintenanceActive) return config;

            const currentRole = roleRef.current;
            if (!currentRole) return config; // role not loaded → allow all
            if (currentRole.roleType === RoleTypeEnum.Admin) return config; // admin → allow all

            const url = config.url ?? '';
            const isExempt = MAINTENANCE_EXEMPT_ENDPOINTS.some((e) => url.toLowerCase().includes(e.toLowerCase()));
            if (isExempt) return config;

            return Promise.reject(new Error('Request blocked: maintenance mode active'));
        });

        return () => {
            axios.interceptors.request.eject(interceptorId);
        };
    }, []);

    fetchRef.current = async () => {
        if (!apiEndpoints?.['getAppStatus']?.url) return;
        if (!loggedInUser) return;
        setIsLoading(true);
        setError(null);
        try {
            const url = `${appSettings.api}${apiEndpoints['getAppStatus'].url}`;
            const response = await axios.get<AppStatusResponse>(url);
            const data = response.data;
            if (data) {
                setReleaseNotes(data.releaseNotes ?? []);
                setMaintenanceMode(data.maintenanceMode ?? null);
                setActiveStatusMessages(data.statusMessages?.filter((m) => m.isActive) ?? []);
                writeMaintenanceToStorage(data.maintenanceMode ?? null);
                networkFailureCountRef.current = 0;
                setApiUnavailable(false);
                // Continue polling while maintenance is active — SignalR may not be connected
                if (data.maintenanceMode?.isMaintenanceActive) {
                    if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
                    retryTimerRef.current = setTimeout(() => {
                        fetchRef.current?.();
                    }, 30000);
                }
            } else {
                setError('Failed to load app status');
            }
        } catch (err: unknown) {
            const axiosError = err as { request?: unknown; response?: unknown };
            if (axiosError.request && !axiosError.response) {
                // Network error — no response received; retry after 3s
                networkFailureCountRef.current++;
                if (networkFailureCountRef.current >= 2) {
                    setApiUnavailable(true);
                }
                const backoffIndex = Math.min(networkFailureCountRef.current - 1, RETRY_BACKOFF_MS.length - 1);
                const delay = RETRY_BACKOFF_MS[backoffIndex];
                if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
                retryTimerRef.current = setTimeout(() => {
                    fetchRef.current?.();
                }, delay);
            } else {
                setError('Failed to load app status');
            }
        } finally {
            setIsLoading(false);
        }
    };

    // One-shot fetch when endpoint becomes available and user is authenticated
    useEffect(() => {
        const endpointUrl = apiEndpoints?.['getAppStatus']?.url;
        if (!endpointUrl) return;
        if (!loggedInUser) return;
        fetchRef.current?.();
    }, [apiEndpoints?.['getAppStatus']?.url, loggedInUser]);

    // Cleanup retry timer on unmount
    useEffect(() => {
        return () => {
            if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
        };
    }, []);

    const refetch = useCallback(() => {
        fetchRef.current?.();
    }, []);

    return (
        <AppStatusContext.Provider value={{ releaseNotes, maintenanceMode, activeStatusMessages, isLoading, error, apiUnavailable, refetch }}>
            {children}
        </AppStatusContext.Provider>
    );
};

export const useAppStatus = () => useContext(AppStatusContext);
